// @/js/autorization/timer.utils.js

/**
 * Creates a simple reusable cooldown timer.
 *
 * Usage:
 * const t = createCooldownTimer({
 *   seconds: 60,
 *   onTick: (secLeft) => { el.textContent = `${secLeft} сек`; },
 *   onDone: () => { button.disabled = false; },
 * });
 * t.start(); t.stop(); t.setSeconds(30);
 */
export const createCooldownTimer = ({
  seconds = 60,
  onTick,
  onDone,
} = {}) => {
  let timerId = null;
  let secondsLeft = 0;

  const safeCall = (fn, ...args) => {
    try {
      if (typeof fn === 'function') fn(...args);
    } catch (e) {}
  };

  const render = () => safeCall(onTick, secondsLeft);

  const stop = () => {
    if (timerId) clearInterval(timerId);
    timerId = null;
    secondsLeft = 0;
    render();
  };

  const start = (sec = seconds) => {
    stop();

    secondsLeft = Math.max(0, Number(sec) || 0);
    render();

    timerId = setInterval(() => {
      secondsLeft = Math.max(0, secondsLeft - 1);
      render();

      if (secondsLeft <= 0) {
        stop();
        safeCall(onDone);
      }
    }, 1000);
  };

  const setSeconds = (sec) => {
    seconds = Number(sec) || seconds;
  };

  const getSecondsLeft = () => secondsLeft;
  const isRunning = () => !!timerId;

  return { start, stop, setSeconds, getSecondsLeft, isRunning };
};

/**
 * Small helper for "disable after N seconds unless locked".
 * If lockedFn() returns true - onDone won't enable.
 */
export const createCooldownWithLock = ({
  seconds = 60,
  onTick,
  onEnable,
  lockedFn,
} = {}) => {
  return createCooldownTimer({
    seconds,
    onTick,
    onDone: () => {
      const locked = typeof lockedFn === 'function' ? !!lockedFn() : false;
      if (!locked) {
        try {
          if (typeof onEnable === 'function') onEnable();
        } catch (e) {}
      }
    },
  });
};
